import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { getERPClient } from "@/lib/erp/client";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { parseLinkSearchResponse, clearLevenshteinCache, type LinkSearchResult } from "@/lib/erp/utils/linkUtils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type LinkFieldSearchProps = {
  doctype: string; // The linked doctype (e.g., "Item Group")
  referenceDoctype?: string; // Parent context doctype (e.g., "Stock Entry") for core link query behavior
  value: string | null;
  onChange: (value: string | null) => void;
  onBlur?: (value: string | null) => void; // Callback when field loses focus with current value
  placeholder?: string;
  disabled?: boolean;
  initialSearch?: string; // Default "" - empty string returns all items from Frappe
  fetchInitialItems?: boolean; // Default true - whether to fetch initial items when popover opens
  className?: string; // Allow custom className for width
  "data-field"?: string; // Data attribute for Cypress selectors
  defaultFilters?: Record<string, any>; // Optional filters (e.g., { disabled: 0 } for Item, undefined for Item Group)
  align?: "start" | "center" | "end"; // Alignment of the popover dropdown (default: "start")
};

/**
 * Async searchable select for Link fields
 * Uses ERPNext's search_link method
 */
export function LinkFieldSearch({
  doctype,
  referenceDoctype,
  value,
  onChange,
  onBlur, // Callback when field loses focus
  placeholder = "Search...",
  disabled = false,
  initialSearch = "", // Empty string returns all items (Frappe skips LIKE filter when txt="")
  fetchInitialItems = true,
  className = "w-[200px]",
  "data-field": dataField,
  defaultFilters, // Optional filters from caller (if not provided, will use smart defaults)
  align = "start", // Default alignment
}: LinkFieldSearchProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Request deduplication cache (TTL: 60 seconds, max 50 entries)
  const requestCache = useRef(new Map<string, { data: LinkSearchResult[]; timestamp: number }>());
  const CACHE_TTL = 60000; // 60 seconds
  const MAX_CACHE_SIZE = 50;

  // Query cancellation system
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestVersionRef = useRef(0);

  // Debounce search term to avoid excessive API calls (200-300ms as recommended)
  const debouncedSearchTerm = useDebounce(searchTerm, 250);

  // ✅ Fix 1: Trim doctype to avoid trailing spaces from schema
  const searchDoctype = (doctype || "").trim();

  // Determine the search text to use
  // When txt="" Frappe returns all records (skips LIKE filter). When txt="%" Frappe
  // searches for literal "%" in names and returns 0 results. So use "" not "%".
  const searchText =
    debouncedSearchTerm.trim().length >= 2
      ? debouncedSearchTerm.trim()
      : fetchInitialItems
        ? initialSearch // Default "" returns all items from Frappe
        : "";

  // effectiveTxt can be empty string — that's valid and means "return all"
  const effectiveTxt = debouncedSearchTerm.trim().length >= 2
    ? debouncedSearchTerm.trim()
    : initialSearch;

  // Stable stringify for cache keys (ensures object key order is deterministic)
  const stableStringify = (obj: any): string => {
    if (!obj || typeof obj !== "object") return String(obj);
    if (Array.isArray(obj)) return JSON.stringify(obj);
    const keys = Object.keys(obj).sort();
    const sorted: Record<string, any> = {};
    for (const k of keys) sorted[k] = obj[k];
    return JSON.stringify(sorted);
  };

  // Doctype change handler - reset everything
  useEffect(() => {
    // Reset all state
    setSearchTerm("");
    setSelectedIndex(-1);
    setOpen(false);
    requestVersionRef.current = 0;
    requestCache.current.clear();
    
    // CRITICAL: Clear Levenshtein cache (different doctypes = different vocabularies)
    clearLevenshteinCache();
    
    // Cancel any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [doctype]);

  // Auto-abort on popover close
  useEffect(() => {
    if (!open && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Fetch search results - show initial results when open, or search when typing
  const { data: results = [], isLoading, error } = useQuery<LinkSearchResult[]>({
    // IMPORTANT: include referenceDoctype + defaultFilters in queryKey to avoid stale/mixed results
    queryKey: [
      "link-search",
      doctype,
      referenceDoctype || null,
      searchText || "empty",
      defaultFilters ? stableStringify(defaultFilters) : null,
    ],
    queryFn: async ({ signal }) => {
      const client = getERPClient();
      
      // Increment version counter for this request
      const currentVersion = ++requestVersionRef.current;
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      
      // Combine signals (React Query + our abort controller)
      const combinedSignal = signal || abortController.signal;
      
      // Check if request was already aborted
      if (combinedSignal.aborted) {
        throw new Error("Request aborted");
      }

      // Only return empty if searchText is empty AND we're not fetching initial items
      // Empty string is valid when fetchInitialItems=true (for initial suggestions)
      if (searchText === "" && !fetchInitialItems) {
        return [];
      }

      try {
        // For User doctype, ignore permissions to allow user selection
        // This is safe because we're only showing user names/emails for selection
        const ignorePermissionsFlag = doctype === "User";

        // CRITICAL FIX: Use debouncedSearchTerm directly, not shadowed variable
        const isUserSearch = debouncedSearchTerm.trim().length >= 2;
        
        // Debug logging for User doctype searches
        if (doctype === "User") {
          console.debug("[LinkFieldSearch] search_link params", {
            doctype,
            txt: searchText,
            ignore_user_permissions: ignorePermissionsFlag,
            reference_doctype: undefined, // Will be undefined for User
          });
        }
        
        // Use configurable filters if provided from caller.
        // Otherwise, only apply a safe default for doctypes we are sure about.
        // Do NOT assume that every doctype has a "disabled" column.
        const filters = defaultFilters ?? (
          searchDoctype === "User"
            ? { enabled: 1 }
            : undefined
        );
        const filtersKey = filters ? stableStringify(filters) : "nofilters";
        const referenceDoctypeKey = referenceDoctype ? referenceDoctype.trim() : "noref";

        // Include permission + filters + referenceDoctype in cache key to prevent stale/mixed results
        const cacheKey = `${doctype}:${referenceDoctypeKey}:${searchText}:${filtersKey}:${ignorePermissionsFlag ? "ignorePerms" : "respectPerms"}`;
        const now = Date.now();
        
        // Check cache first (must happen AFTER cacheKey is computed)
        const cached = requestCache.current.get(cacheKey);
        if (cached && (now - cached.timestamp) < CACHE_TTL) {
          // Check version before returning cached data
          if (currentVersion === requestVersionRef.current) {
            return cached.data;
          }
          // Stale request - abort
          throw new Error("Request superseded");
        }
        
        console.debug("[LinkFieldSearch] Calling search_link", {
          doctype: searchDoctype,
          originalDoctype: doctype,
          referenceDoctype,
          txt: effectiveTxt,
          searchText,
          fetchInitialItems,
          searchTerm,
          debouncedSearchTerm,
          open,
          filters, // Log the actual filters being sent
        });
        
        // Build request params - only include filters if defined
        const requestParams: any = {
          doctype: searchDoctype, // ✅ Trimmed doctype
          txt: effectiveTxt, // Empty string = "return all", Frappe skips LIKE filter
          page_length: isUserSearch ? 15 : 20,
          ignore_user_permissions: ignorePermissionsFlag,
          // IMPORTANT: reference_doctype should be the *parent* context doctype, not the target doctype.
          reference_doctype: doctype === "User" ? undefined : (referenceDoctype?.trim() || undefined),
          signal: combinedSignal,
        };
        
        // ✅ Only include filters if defined (don't send undefined)
        if (filters !== undefined) {
          requestParams.filters = filters;
        }
        
        const response = await client.call<any>(
          "frappe.desk.search.search_link",
          requestParams
        );
        
        console.debug("[LinkFieldSearch] search_link response", {
            doctype,
          responseLength: Array.isArray(response) ? response.length : 'not array',
          responseType: typeof response,
        });

        // Check version before processing
        if (currentVersion !== requestVersionRef.current) {
          throw new Error("Request superseded");
        }

        // Use shared utility to parse and normalize response
        // Pass doctype context for label parsing (e.g., Price List needs special handling)
        const parsed = isUserSearch
          ? parseLinkSearchResponse(response, {
              searchInput: debouncedSearchTerm.trim(),
              enableRanking: true,
              doctype: searchDoctype,
            })
          : parseLinkSearchResponse(response, {
              doctype: searchDoctype,
            });

        // Debug logging for User doctype results
        if (doctype === "User") {
          console.debug("[LinkFieldSearch] results length", parsed.length);
        }

        // Store in cache
        requestCache.current.set(cacheKey, { data: parsed, timestamp: now });
        
        // Evict oldest entries if cache is full
        if (requestCache.current.size > MAX_CACHE_SIZE) {
          const oldestKey = Array.from(requestCache.current.entries())
            .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0];
          requestCache.current.delete(oldestKey);
        }

        return parsed;
      } catch (err: any) {
        // Don't log aborted requests as errors
        if (err?.name === "AbortError" || err?.message === "Request aborted" || err?.message === "Request superseded") {
          throw err;
        }
        
        // ✅ Improve error visibility - log backend errors so they're obvious
        // Calculate filters again for error logging (same logic as above)
        const errorFilters = defaultFilters ?? (
          searchDoctype === "User"
            ? { enabled: 1 }
            : undefined
        );
        
        const errorMessage = err?.message || String(err);
        const isUnknownColumnError = errorMessage.includes("Unknown column") && errorMessage.includes("disabled");
        
        console.error("[LinkFieldSearch] search_link failed", {
          doctype: searchDoctype,
          effectiveTxt,
          filters: errorFilters,
          error: errorMessage,
          errorName: err?.name,
          isUnknownColumnError,
          hint: isUnknownColumnError 
            ? `Backend is trying to filter by 'disabled' column that doesn't exist in ${searchDoctype}. This is a Frappe backend issue - the search_link method is unconditionally adding disabled=0 filter. See docs/engineering/search-link-disabled-column-fix.md for backend fix.`
            : undefined,
        });
        
        // For other errors, return empty array
        return [];
      }
    },
    // ✅ Fix 2: Ensure query is enabled on click/typing for filters
    enabled: open && (!!effectiveTxt || fetchInitialItems), // Run when popover is open and we have text or fetchInitialItems is true
    staleTime: 0, // Custom cache handles deduplication
    gcTime: 60000, // Keep in cache for 1 minute
  });

  // Debug: Log query state changes
  useEffect(() => {
    if (results.length > 0) {
      console.debug("[LinkFieldSearch] Query succeeded", {
        doctype: searchDoctype,
        searchText: effectiveTxt,
        resultsCount: results.length,
        open,
        fetchInitialItems,
      });
    }
    if (error) {
      console.error("[LinkFieldSearch] Query error", {
        doctype: searchDoctype,
        searchText: effectiveTxt,
        error: error.message,
        open,
        fetchInitialItems,
      });
    }
  }, [results.length, error, searchDoctype, effectiveTxt, open, fetchInitialItems]);

  // Fetch selected value label if value exists (for display when not searching)
  // Uses get_link_title which respects show_title_field_in_link + title_field on the DocType.
  // e.g. Company "CSide & Sub Scrub - 0j85q3282r" → displays "CSide & Sub Scrub"
  const { data: selectedLabel } = useQuery<string>({
    queryKey: ["link-title", doctype, value],
    queryFn: async () => {
      if (!value) return "";

      const client = getERPClient();
      try {
        const title = await client.call<string>("frappe.desk.search.get_link_title", {
          doctype,
          docname: value,
        });
        return title || value;
      } catch {
        return value;
      }
    },
    enabled: !!value && !searchTerm, // Only fetch when we have a value and user isn't searching
  });

  // Virtual scrolling (only for >20 results)
  const shouldUseVirtualScroll = results.length > 20;
  const virtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 36, // CommandItem height ~36px
    overscan: 5,
    enabled: shouldUseVirtualScroll && open,
  });

  // Sync selectedIndex with virtual scroll
  useEffect(() => {
    if (shouldUseVirtualScroll && selectedIndex !== -1 && selectedIndex < results.length) {
      virtualizer.scrollToIndex(selectedIndex, {
        align: "auto",
        behavior: "smooth",
      });
    }
  }, [selectedIndex, shouldUseVirtualScroll, virtualizer, results.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === "Enter" && selectedIndex !== -1 && results[selectedIndex]) {
        e.preventDefault();
        handleSelect(results[selectedIndex].value);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, results, selectedIndex]);

  // ✅ Fix 3: Ensure handleSelect passes string docname, not object
  const handleSelect = (selectedValue: string) => {
    console.debug("[LinkFieldSearch] handleSelect called", {
      selectedValue,
      valueType: typeof selectedValue,
      isString: typeof selectedValue === "string",
    });
    
    // ✅ Must be string, not object
    if (typeof selectedValue !== "string") {
      console.error("[LinkFieldSearch] ERROR: selectedValue is not a string!", selectedValue);
      return;
    }
    
    onChange(selectedValue); // ✅ String docname
    setOpen(false);
    setSearchTerm(""); // Clear search term after selection
    setSelectedIndex(-1); // Reset selection
  };

  // Display value: 
  // - If user is typing (searchTerm exists), show searchTerm
  // - If value is selected and no searchTerm, show selectedLabel
  // - Otherwise show placeholder
  const displayValue = searchTerm || (value && !searchTerm ? (selectedLabel || value) : "");

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              data-field={dataField}
              value={displayValue}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchTerm(newValue); // Update search term immediately
                setSelectedIndex(-1); // Reset selection on type
                // If user clears the input, clear the value
                if (!newValue) {
                  onChange(null);
                }
                // Keep popover open when typing or when cleared
                setOpen(true);
              }}
              onFocus={() => {
                // Always open popover on focus to show initial results
                setOpen(true);
              }}
              onBlur={(e) => {
                // Don't close if clicking within the popover portal
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (relatedTarget?.closest('[data-radix-popper-content-wrapper]') || relatedTarget?.closest('[cmdk-list]')) {
                  e.preventDefault();
                  return;
                }

                // Call onBlur prop with current value (onChange is called before blur in normal flow)
                if (onBlur) {
                  onBlur(value);
                }
              }}
              placeholder={placeholder}
              disabled={disabled}
              className={cn("w-full pr-8", className)}
            />
            <ChevronDown
              className={cn(
                "absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none transition-transform",
                open && "rotate-180"
              )}
            />
          </div>
        </PopoverAnchor>
        {open && (
          <PopoverContent
            // NOTE: do NOT spread the `className` prop onto the PopoverContent.
            // Callers use `className` to style the input (e.g. MODAL_LINK_FIELD_SEARCH_CLASSES
            // with `!bg-transparent`, `!shadow-none`, `border-0`) which would otherwise
            // make the popover invisible inside modals. Only apply width here.
            style={{ width: "var(--radix-popover-trigger-width, 240px)" }}
            className="!w-[400px] max-w-[92vw] p-0 overflow-hidden rounded-xl shadow-xl backdrop-blur-xl bg-white/98 dark:bg-gray-900/98 border border-gray-300 dark:border-gray-700 ring-1 ring-black/[0.02]"
            align={align}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              // The input lives inside PopoverAnchor, not PopoverContent.
              // Radix DismissableLayer treats anchor clicks as "outside" and
              // fires onOpenChange(false), causing the dropdown to flash-close
              // on the very first click. Prevent dismiss when clicking the input.
              const target = e.target as Node;
              if (inputRef.current?.contains(target)) {
                e.preventDefault();
              }
            }}
          >
            <Command shouldFilter={false} className="bg-transparent">
              <CommandList
                ref={listRef}
                className={shouldUseVirtualScroll ? "max-h-[300px] overflow-auto" : ""}
              >
                {isLoading && (
                  <CommandEmpty>Loading...</CommandEmpty>
                )}
                {error && (
                  <CommandEmpty>
                    {(() => {
                      const errorMessage = error?.message || String(error || "");
                      const isUnknownColumnError = errorMessage.includes("Unknown column") && errorMessage.includes("disabled");
                      return isUnknownColumnError
                        ? `Backend error: ${doctype} table missing 'disabled' column. Contact admin.`
                        : "Error loading results";
                    })()}
                  </CommandEmpty>
                )}
                {!isLoading && !error && results.length === 0 && searchTerm.length >= 2 && (
                  <CommandEmpty>No results found</CommandEmpty>
                )}
                {!isLoading && !error && results.length === 0 && searchTerm.length < 2 && fetchInitialItems && (
                  <CommandEmpty>No items available</CommandEmpty>
                )}
                {!isLoading && !error && results.length > 0 && (
                  <CommandGroup>
                    {shouldUseVirtualScroll ? (
                      // Virtual scrolling for >20 results
                      <div
                        style={{
                          height: `${virtualizer.getTotalSize()}px`,
                          width: "100%",
                          position: "relative",
                        }}
                      >
                        {virtualizer.getVirtualItems().map((virtualItem) => {
                          const result = results[virtualItem.index];
                          const displayLabel = result.label || result.description || result.value || "";
                          const resultValue = result.value || "";
                          const isSelected = selectedIndex === virtualItem.index;
                          return (
                            <CommandItem
                              key={`${resultValue}-${searchText}-${virtualItem.index}`}
                              value={resultValue}
                              onSelect={() => handleSelect(resultValue)}
                              // Measure real height so long, wrapping labels don't
                              // overlap (rows grow to fit instead of a fixed 36px).
                              data-index={virtualItem.index}
                              ref={virtualizer.measureElement}
                              className={cn(
                                "items-start",
                                isSelected && "bg-accent"
                              )}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                transform: `translateY(${virtualItem.start}px)`,
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 mt-0.5 h-4 w-4 shrink-0",
                                  value === resultValue ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="whitespace-normal break-words leading-snug" title={displayLabel}>
                                {displayLabel}
                              </span>
                            </CommandItem>
                          );
                        })}
                      </div>
                    ) : (
                      // Regular rendering for ≤20 results
                      results.map((result, index) => {
                        const displayLabel = result.label || result.description || result.value || "";
                        const resultValue = result.value || "";
                        const isSelected = selectedIndex === index;
                        return (
                          <CommandItem
                            key={`${resultValue}-${searchText}-${index}`}
                            value={resultValue}
                            onSelect={() => handleSelect(resultValue)}
                            className={cn(
                              isSelected && "bg-accent"
                            )}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                value === resultValue ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span title={resultValue}>{displayLabel}</span>
                          </CommandItem>
                        );
                      })
                    )}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}

